import { UnknownAction } from "redux"
import { ThunkDispatch } from "redux-thunk"
import i18n from "i18next"
import { getDaysUntil } from "utils/dateUtils"
import { FOLLOW_REQUEST, ITEM_LIKED, NEW_COMMENT, UPCOMING_BIRTHDAY, TRUE_WISH, PREMIUM_EXPIRING } from "constants/notifications"
import { store } from "appRedux/store"
import { updateShowCommentsBottomSheet, updateShowGeniesList } from "appRedux/slices/wishListSlice"
import {
  BaseNotification,
  ActorNotification,
  ItemNotification,
  NotificationType,
  TrueWishNotification,
  UpcomingBirthdayNotification,
  WishamNotification
} from "types/notification.types"

export const messageHandlers: Partial<Record<NotificationType, (notification: WishamNotification) => Record<string, any>>> = {
  [FOLLOW_REQUEST]: (notif) => {
    const followRequestNotification = notif as ActorNotification
    return { handle: followRequestNotification.handle, count: 1 }
  },
  [ITEM_LIKED]: (notif) => {
    const itemLikedNotification = notif as ItemNotification
    return { handle: itemLikedNotification.handle, itemName: itemLikedNotification?.item?.name || "" }
  },
  [NEW_COMMENT]: (notif) => {
    const newCommentNotification = notif as ItemNotification
    return { handle: newCommentNotification.handle, itemName: newCommentNotification?.item?.name || "" }
  },
  [UPCOMING_BIRTHDAY]: (notif) => {
    const upcomingBirthdayNotification = notif as UpcomingBirthdayNotification
    return {
      handle: upcomingBirthdayNotification.handle,
      remainingDays: getDaysUntil(upcomingBirthdayNotification.date, upcomingBirthdayNotification.birthday, true)
    }
  },
  [TRUE_WISH]: (notif) => {
    const trueWishNotification = notif as TrueWishNotification
    return { handle: trueWishNotification.handle, itemName: trueWishNotification.item.name, count: trueWishNotification.geniesCount - 1 }
  },
  [PREMIUM_EXPIRING]: (notif) => {
    const premiumExpiringNotification = notif as BaseNotification
    const state = store.getState()
    const expireDate = state.premium.expireDate
    return { remainingDays: !!expireDate ? getDaysUntil(premiumExpiringNotification.date, expireDate) : i18n.t("general.some") }
  }
}

export const notificationClickHandlers: Partial<Record<
  NotificationType,
  (notification: WishamNotification, dispatch: ThunkDispatch<any, any, UnknownAction>) => Record<string, any>
>> = {
  [ITEM_LIKED]: (notif, dispatch) => handleItemLikedNotificationClicked(notif as ItemNotification),
  [NEW_COMMENT]: (notif, dispatch) => handleNewCommentNotificationClicked(notif as ItemNotification, dispatch),
  [TRUE_WISH]: (notif, dispatch) => handleTrueWishNotificationClicked(notif as TrueWishNotification, dispatch)
}

export const notificationValidators: Partial<Record<NotificationType, (notification: WishamNotification) => boolean>> = {
  [UPCOMING_BIRTHDAY]: (notif) => {
    const upcomingBirthdayNotification = notif as UpcomingBirthdayNotification
    return !!upcomingBirthdayNotification.birthday
  }
}

function handleItemLikedNotificationClicked(notification: ItemNotification) {
  return {
    id: notification.userId,
    handle: notification.owner.handle,
    itemId: notification.item.id
  }
}

function handleNewCommentNotificationClicked(notification: ItemNotification, dispatch: ThunkDispatch<any, any, UnknownAction>) {
  dispatch(updateShowCommentsBottomSheet(true))
  return {
    id: notification.userId,
    handle: notification.owner.handle,
    itemId: notification.item.id
  }
}

function handleTrueWishNotificationClicked(notification: ItemNotification, dispatch: ThunkDispatch<any, any, UnknownAction>) {
  dispatch(updateShowGeniesList(true))
  return {
    id: notification.userId,
    handle: notification?.handle,
    itemId: notification.item.id
  }
}

export function isActorNotification(notif: WishamNotification): notif is ActorNotification {
  return "handle" in notif && !!notif.handle
}
